import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useParams, useOutletContext } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { lang } from "config"
import Input from "components/forms/input"
import ErrorMessage from "components/forms/error-message"
import SelectSearchable from "components/forms/select-searchable"
import { useAuth } from "contexts/auth-context"
import { getBranchList, getGenericSupplier } from "helpers/general-helper"
import { Plus } from "components/icons/outline"

function NonPoProductPicker({ disabled, outletId, onPick }) {
  const inventoryApi = process.env.REACT_APP_INVENTORY_API_URL
  const perPage = 30

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [page, setPage] = useState(1)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const debounceRef = useRef()

  const fetchPage = async (targetPage = 1, append = false) => {
    if (!inventoryApi || !outletId) return
    setLoading(true)
    try {
      const url = new URL(`${inventoryApi}/ps_list`)
      const params = {
        page: targetPage,
        per_page: perPage,
        search: query,
        outlet_id: outletId,
        only_in_stock: false,
        with_attributes: false,
      }
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v)
      })

      const res = await fetch(url.toString())
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      const rows = Array.isArray(json?.data) ? json.data : []
      setItems((prev) => (append ? [...prev, ...rows] : rows))
      setHasMore(Boolean(json?.meta?.next_page_url) || rows.length === perPage)
      setPage(targetPage)
    } catch {
      toast.error("Gagal mengambil daftar produk.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open || !outletId) return
    fetchPage(1, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, outletId])

  useEffect(() => {
    if (!open) return
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchPage(1, false)
    }, 300)
    return () => clearTimeout(debounceRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const onScroll = (event) => {
    const el = event.currentTarget
    if (!loading && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      fetchPage(page + 1, true)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        disabled={disabled || !outletId}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg border ${
          disabled || !outletId
            ? "border-neutral-200 text-neutral-400 cursor-not-allowed"
            : "border-neutral-300 text-neutral-700 hover:bg-neutral-50"
        }`}
      >
        <Plus className="w-4 h-4" stroke={1.5} />
        Tambah item luar PO
      </button>
    )
  }

  return (
    <div className="space-y-2 border border-neutral-200 rounded-xl p-3 bg-neutral-50">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-neutral-700">Pilih produk yang datang tapi tidak ada di PO</div>
        <button
          type="button"
          onClick={() => {
            setOpen(false)
            setQuery("")
          }}
          className="text-[11px] text-neutral-500 hover:text-neutral-800"
        >
          Tutup
        </button>
      </div>
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Cari SKU / Nama / Brand / Model..."
      />
      <div className="h-60 overflow-auto border rounded-xl bg-white" onScroll={onScroll}>
        {items.map((row) => {
          const id = row.product_unit_id ?? row.id
          const name = row.product_unit_long_name ?? row.product_unit ?? row.name ?? "-"
          const sku = row.sku ?? "-"
          const brand = row.product_brand ?? row.brand ?? "-"
          return (
            <button
              key={id}
              type="button"
              className="w-full px-3 py-2 text-left border-b last:border-b-0 hover:bg-neutral-50"
              onClick={() => {
                onPick({
                  product_unit_id: String(id),
                  product_unit_name: name,
                })
                setOpen(false)
                setQuery("")
              }}
            >
              <div className="text-sm font-medium text-neutral-900">{name}</div>
              <div className="text-xs text-neutral-500">{sku} • {brand}</div>
            </button>
          )
        })}
        {loading && <div className="p-3 text-xs text-neutral-500">Memuat...</div>}
        {!loading && items.length === 0 && (
          <div className="p-3 text-xs text-neutral-500">Tidak ada hasil</div>
        )}
      </div>
    </div>
  )
}

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function moneyFmt(v) {
  const n = num(v)
  if (!n) return "-"
  return Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }).format(n)
}

async function apiFetch(path, { method = "GET", body } = {}) {
  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
  const url = `${base}${path}`

  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : null,
  })

  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = json?.message || "Request gagal"
    throw new Error(msg)
  }
  return json
}

/**
 * Goods Receipt Form Page
 * Route example:
 * - Create (manual): /goods-receipts/create
 * - Edit: /goods-receipts/:id/edit
 *
 * Optional query:
 * - ?po_id=...  (prefill PO)
 */
export default function GoodsReceiptFormPage() {
  const { id } = useParams()
  const isEdit = Boolean(id)

  const { can, currentBranch } = useAuth()
  const { title, transition, setTransition, setTitle } = useOutletContext()
  const navigate = useNavigate()
  const location = useLocation()

  const branchSelection = getBranchList()
  const supplierSelection = getGenericSupplier()

  const qs = new URLSearchParams(location.search)
  const poIdFromQuery = qs.get("po_id") || null

  const [errors, setErrors] = useState({})

  // Header
  const [grId, setGrId] = useState(null)
  const [grNumber, setGrNumber] = useState(null)
  const [status, setStatus] = useState("DRAFT")

  const [poId, setPoId] = useState(poIdFromQuery)
  const [outletId, setOutletId] = useState(currentBranch?.id ?? null)
  const [outletName, setOutletName] = useState(currentBranch?.name ?? null)
  const [supplierId, setSupplierId] = useState(null)
  const [supplierName, setSupplierName] = useState(null)

  const [receiptDate, setReceiptDate] = useState(todayISO())
  const [supplierDoNumber, setSupplierDoNumber] = useState("")
  const [remarks, setRemarks] = useState("")

  // Items (manual input mirip PO)
  const [items, setItems] = useState([]) // {id?, po_item_id?, product_unit_id, qty_received, batch_no, expired_date}
  const [productUnitIdInput, setProductUnitIdInput] = useState("")
  const [qtyInput, setQtyInput] = useState(1)
  const [batchInput, setBatchInput] = useState("")
  const [expiredInput, setExpiredInput] = useState("")

  const isDraft = String(status || "").toUpperCase() === "DRAFT"
  const isLocked = !isDraft

  useEffect(() => {
    setTitle?.(isEdit ? "Edit Goods Receipt" : "Create Goods Receipt")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  useEffect(() => {
    if (transition?.reverse) setTransition?.({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load GR if edit
  useEffect(() => {
    const run = async () => {
      if (!isEdit) return
      try {
        nProgress.start()
        const res = await apiFetch(`/goods-receipts/${id}`)
        const gr = res?.data || res

        setGrId(gr?.id ?? id)
        setGrNumber(gr?.gr_number ?? null)
        setStatus(gr?.status ?? "DRAFT")

        setPoId(gr?.po_id ?? null)
        setOutletId(gr?.outlet_id ?? null)
        setReceiptDate((gr?.receipt_date || "").slice(0, 10) || todayISO())
        setSupplierDoNumber(gr?.supplier_do_number ?? "")
        setRemarks(gr?.remarks ?? "")
        setSupplierId(gr?.purchase_order.supplier_id)

        const b = branchSelection?.find((x) => String(x.id) === String(gr?.outlet_id))
        setOutletName(b?.name ?? null)

        // supplier_id tidak ada di GR model kamu (di service gak disimpan).
        // biasanya supplier bisa diambil dari PO details (kalau UI butuh).
        // Untuk sementara: keep supplier selection manual.
        // Kalau kamu punya endpoint PO show, bisa fetch PO untuk ambil supplier_id & name.

        const rows = Array.isArray(gr?.items) ? gr.items : []
        setItems(
          rows.map((r) => ({
            id: r?.id,
            po_item_id: r?.po_item_id ?? null,
            product_unit:r?.product_unit.name,
            product_unit_id: String(r?.product_unit_id ?? ""),
            qty_received: Number(r?.qty_received ?? 0),
            batch_no: r?.batch_no ?? "",
            expired_date: r?.expired_date ? String(r.expired_date).slice(0, 10) : "",
          }))
        )
      } catch (e) {
        toast.error(e.message || "Gagal load GR")
      } finally {
        nProgress.done()
      }
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  const totals = useMemo(() => {
    const qtyTotal = items.reduce((acc, r) => acc + num(r.qty_received), 0)
    return { qtyTotal }
  }, [items])

  const validate = () => {
    const e = {}
    if (!poId) e.po_id = "PO ID wajib diisi"
    if (!outletId) e.outlet_id = "Outlet wajib dipilih"
    if (!receiptDate) e.receipt_date = "Receipt date wajib diisi"
    if (!items.length) e.items = "Minimal 1 item"

    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      if (!it.product_unit_id) e[`items.${i}.product_unit_id`] = "product_unit_id wajib"
      if (num(it.qty_received) <= 0) e[`items.${i}.qty_received`] = "qty_received harus > 0"
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const buildHeaderPayload = () => ({
    receipt_date: receiptDate,
    supplier_do_number: supplierDoNumber || null,
    remarks: remarks || null,
  })

  const buildItemsPayload = () => ({
    allow_delete_missing: true,
    items: items.map((it) => ({
      id: it.id || undefined,
      po_item_id: it.po_item_id || undefined,
      product_unit_id: it.product_unit_id,
      product_unit: it.product_unit,
      qty_received: num(it.qty_received),
      batch_no: it.batch_no || null,
      expired_date: it.expired_date || null,
    })),
  })

  const onAddItem = () => {
    const pid = String(productUnitIdInput || "").trim()
    const qty = num(qtyInput || 0)

    if (!pid) return toast.error("Isi product_unit_id dulu")
    if (qty <= 0) return toast.error("qty_received harus > 0")

    const existsIndex = items.findIndex((x) => String(x.product_unit_id) === pid)

    if (existsIndex >= 0) {
      const next = [...items]
      next[existsIndex] = {
        ...next[existsIndex],
        qty_received: num(next[existsIndex].qty_received) + qty,
        batch_no: batchInput || next[existsIndex].batch_no,
        expired_date: expiredInput || next[existsIndex].expired_date,
      }
      setItems(next)
      toast.success("Qty item ditambahkan")
    } else {
      setItems((prev) => [
        ...prev,
        {
          product_unit_id: pid,
          qty_received: qty,
          batch_no: batchInput || "",
          expired_date: expiredInput || "",
        },
      ])
      toast.success("Item ditambahkan")
    }

    setProductUnitIdInput("")
    setQtyInput(1)
    setBatchInput("")
    setExpiredInput("")
  }

  const onRemoveItem = (product_unit_id) => {
    setItems((prev) => prev.filter((x) => String(x.product_unit_id) !== String(product_unit_id)))
  }

  const onAddNonPoItem = (picked) => {
    const pid = String(picked?.product_unit_id || "").trim()
    if (!pid) return
    const existsIndex = items.findIndex((x) => String(x.product_unit_id) === pid)
    if (existsIndex >= 0) {
      toast.error("Produk ini sudah ada di daftar item")
      return
    }
    setItems((prev) => [
      ...prev,
      {
        po_item_id: null,
        product_unit_id: pid,
        product_unit: picked?.product_unit_name || pid,
        qty_received: 1,
        batch_no: "",
        expired_date: "",
      },
    ])
    toast.success("Item luar PO ditambahkan")
  }

  const onUpdateItem = (product_unit_id, patch) => {
    setItems((prev) =>
      prev.map((it) =>
        String(it.product_unit_id) === String(product_unit_id) ? { ...it, ...patch } : it
      )
    )
  }

  /**
   * SAVE DRAFT:
   * - If create: POST /goods-receipts
   * - If edit: PUT header + PUT items
   */
  const onSaveDraft = async () => {
    if (!validate()) return

    try {
      nProgress.start()

      if (!isEdit) {
        // create draft
        const payload = {
          po_id: poId,
          outlet_id: outletId,
          receipt_date: receiptDate,
          supplier_do_number: supplierDoNumber || null,
          remarks: remarks || null,
          items: buildItemsPayload().items,
        }

        const res = await apiFetch(`/goods-receipts`, { method: "POST", body: payload })
        const newId = res?.data?.id || res?.id

        toast.success(res?.message || "GR draft created")

        if (newId) {
          navigate(`/goods-receipt/${newId}/edit`, {
            replace: true,
            state: { back: title, from: location?.pathname, transition: "slide" },
          })
        }
      } else {
        // update header then items (draft only)
        await apiFetch(`/goods-receipts/${id}/header`, { method: "PUT", body: buildHeaderPayload() })
        const resItems = await apiFetch(`/goods-receipts/${id}/items`, { method: "PUT", body: buildItemsPayload() })

        toast.success("GR draft saved")
        // refresh state items if backend adjusts
        const gr = resItems?.data || null
        if (gr?.items) {
          setItems(
            gr.items.map((r) => ({
              id: r?.id,
              po_item_id: r?.po_item_id ?? null,
              product_unit_id: String(r?.product_unit_id ?? ""),
              product_unit:String(r?.product_unit?.name),
              qty_received: Number(r?.qty_received ?? 0),
              batch_no: r?.batch_no ?? "",
              expired_date: r?.expired_date ? String(r.expired_date).slice(0, 10) : "",
            }))
          )
        }
      }
    } catch (e) {
      toast.error(e.message || "Gagal save GR")
    } finally {
      nProgress.done()
    }
  }

  const onPost = async () => {
    if (!isEdit) return toast.error("Simpan draft dulu sebelum POST")
    if (!validate()) return

    if (!window.confirm(`POST Goods Receipt ${grNumber || ""}?`)) return

    try {
      nProgress.start()

      // ensure latest save before posting
      await apiFetch(`/goods-receipts/${id}/header`, { method: "PUT", body: buildHeaderPayload() })
      await apiFetch(`/goods-receipts/${id}/items`, { method: "PUT", body: buildItemsPayload() })

      const res = await apiFetch(`/goods-receipts/${id}/post`, { method: "POST" })
      toast.success(res?.message || "GR posted")
      setStatus("POSTED")
    } catch (e) {
      toast.error(e.message || "Gagal POST GR")
    } finally {
      nProgress.done()
    }
  }

  const onCancel = async () => {
    if (!isEdit) return
    const reason = window.prompt("Alasan cancel (optional):", "")
    if (reason === null) return

    try {
      nProgress.start()
      const res = await apiFetch(`/goods-receipts/${id}/cancel`, {
        method: "POST",
        body: reason ? { reason } : null,
      })
      toast.success(res?.message || "GR cancelled")
      setStatus("CANCELLED")
    } catch (e) {
      toast.error(e.message || "Gagal cancel GR")
    } finally {
      nProgress.done()
    }
  }

  return (
    <div className="pb-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">
            {isEdit ? "Edit Goods Receipt" : "Create Goods Receipt"}{" "}
            <span className="text-xs font-medium text-neutral-500">(GR)</span>
          </h1>
          <p className="text-xs text-neutral-500">
            Flow: PO Approved ➜ GR Draft ➜ Post (update qty_received PO items).
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/goods-receipt"
            className="px-4 py-3 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
            state={{ back: title, from: location?.pathname, transition: "slide" }}
          >
            Back
          </Link>

          {/* {can?.("goods-receipt.cancel") && isEdit && status !== "CANCELLED" && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-3 text-xs font-semibold text-white bg-red-600 rounded-xl hover:bg-red-500"
            >
              Cancel
            </button>
          )} */}

          {can?.("goods-receipt.post") && isEdit && String(status).toUpperCase() === "DRAFT" && (
            <button
              type="button"
              onClick={onPost}
              className="px-4 py-3 text-xs font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-500"
            >
              Post
            </button>
          )}

          {(can?.("goods-receipt.create") || can?.("goods-receipt.update")) && (
            <button
              type="button"
              onClick={onSaveDraft}
              disabled={isLocked && isEdit}
              className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                isLocked && isEdit ? "bg-neutral-400 cursor-not-allowed" : "bg-neutral-900 hover:bg-neutral-800"
              }`}
            >
              Save Draft
            </button>
          )}
        </div>
      </div>

      {/* Main Form */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-neutral-700">GR Number</label>
              <Input disabled value={grNumber || "Generated by system"} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">{lang.status}</label>
              <Input disabled value={status || "DRAFT"} />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">
                PO ID<sup className="text-xs text-red-700"> *</sup>
              </label>
              <input
                disabled={isEdit} // po_id jangan berubah setelah create
                value={poId || ""}
                onChange={(e) => setPoId(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="PO UUID / ID"
              />
              <ErrorMessage error={errors.po_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                {lang.outlet_name}<sup className="text-xs text-red-700"> *</sup>
              </label>
              <SelectSearchable
                disabled={isEdit} // outlet mengikuti PO (di service strict)
                filter={(value) => value.name}
                onChange={(value) => {
                  const b = branchSelection?.find((x) => String(x.id) === String(value))
                  setOutletId(value)
                  setOutletName(b?.name ?? null)
                }}
                selection={branchSelection}
                isLoading={false}
                value={outletId}
                keyValue={(value) => value.id}
                title={(value) => value.name}
                placeholder="-- Select Outlet --"
              />
              <ErrorMessage error={errors.outlet_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">Supplier</label>
              <SelectSearchable
                disabled={true}
                filter={(value) => value.name}
                onChange={(value) => {
                  setSupplierId(value)
                  const s = supplierSelection?.find((x) => String(x.id) === String(value))
                  setSupplierName(s?.name ?? null)
                }}
                selection={supplierSelection}
                isLoading={false}
                value={supplierId}
                keyValue={(value) => value.id}
                title={(value) => value.name}
                placeholder={supplierName ? supplierName : "-- (Optional) from PO --"}
              />
              <div className="text-[11px] text-neutral-500 mt-1">
                * Supplier idealnya diambil dari PO detail (kalau kamu mau, aku tambahkan fetch PO untuk hydrate supplier).
              </div>
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                Receipt Date<sup className="text-xs text-red-700"> *</sup>
              </label>
              <input
                type="date"
                disabled={isLocked && isEdit}
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
              <ErrorMessage error={errors.receipt_date} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">Supplier DO Number</label>
              <input
                disabled={isLocked && isEdit}
                value={supplierDoNumber}
                onChange={(e) => setSupplierDoNumber(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="SJ-001 / DO-xxx"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">{lang.notes}</label>
              <textarea
                disabled={isLocked && isEdit}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-neutral-900">Items</div>
              {/* <div className="text-xs text-neutral-500">
                Input: product_unit_id + qty_received + batch_no + expired_date.
              </div> */}
            </div>
            <div className="text-xs text-neutral-600">
              Qty Total: <b>{totals.qtyTotal}</b>
            </div>
          </div>

          <ErrorMessage error={errors.items} />

          {/* Tambah item luar PO */}
          <NonPoProductPicker
            disabled={isEdit && isLocked}
            outletId={outletId}
            onPick={onAddNonPoItem}
          />

          {/* Add controls */}
          {/* <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4">
              <label className="block text-xs text-neutral-700">Product Unit ID</label>
              <input
                disabled={isEdit && isLocked}
                value={productUnitIdInput}
                onChange={(e) => setProductUnitIdInput(e.target.value)}
                className="block w-full px-3 py-2 mt-1 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="PRU_..."
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">Qty Received</label>
              <input
                type="number"
                min={1}
                disabled={isEdit && isLocked}
                value={qtyInput}
                onChange={(e) => setQtyInput(e.target.value)}
                className="block w-full px-3 py-2 mt-1 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>

            <div className="col-span-3">
              <label className="block text-xs text-neutral-700">Batch No</label>
              <input
                disabled={isEdit && isLocked}
                value={batchInput}
                onChange={(e) => setBatchInput(e.target.value)}
                className="block w-full px-3 py-2 mt-1 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="B001"
              />
            </div>

            <div className="col-span-3">
              <label className="block text-xs text-neutral-700">Expired Date</label>
              <input
                type="date"
                disabled={isEdit && isLocked}
                value={expiredInput}
                onChange={(e) => setExpiredInput(e.target.value)}
                className="block w-full px-3 py-2 mt-1 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>

            <div className="col-span-12">
              <button
                type="button"
                disabled={isEdit && isLocked}
                onClick={onAddItem}
                className={`w-full inline-flex justify-center items-center gap-2 px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  isEdit && isLocked ? "bg-neutral-400 cursor-not-allowed" : "bg-neutral-900 hover:bg-neutral-800"
                }`}
              >
                <Plus className="w-4 h-4" stroke={1.5} />
                Add Item
              </button>
            </div>
          </div> */}

          {/* Items Table */}
          <div className="overflow-x-auto border border-neutral-200 rounded-xl">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Product Unit</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Sumber</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty Received</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Batch</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Expired</th>
                  <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Action</th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-neutral-200">
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-xs text-center text-neutral-500">
                      Belum ada item
                    </td>
                  </tr>
                )}

                {items.map((it, idx) => {
                  const isNonPo = !it.po_item_id
                  return (
                  <tr key={it.product_unit_id}>
                    <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                      {it.product_unit || it.product_unit_id}
                      {errors[`items.${idx}.product_unit_id`] && (
                        <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.product_unit_id`]}</div>
                      )}
                    </td>

                    <td className="px-3 py-4 text-xs whitespace-nowrap">
                      {isNonPo ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800">
                          Luar PO
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-neutral-100 text-neutral-700">
                          PO
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      <input
                        type="number"
                        min={1}
                        value={it.qty_received}
                        disabled={isEdit && isLocked}
                        onChange={(e) => onUpdateItem(it.product_unit_id, { qty_received: e.target.value })}
                        className="w-28 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                      />
                      {errors[`items.${idx}.qty_received`] && (
                        <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.qty_received`]}</div>
                      )}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      <input
                        value={it.batch_no || ""}
                        disabled={isEdit && isLocked}
                        onChange={(e) => onUpdateItem(it.product_unit_id, { batch_no: e.target.value })}
                        className="w-36 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                      />
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      <input
                        type="date"
                        value={it.expired_date || ""}
                        disabled={isEdit && isLocked}
                        onChange={(e) => onUpdateItem(it.product_unit_id, { expired_date: e.target.value })}
                        className="w-40 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                      />
                    </td>

                    <td className="px-3 py-4 text-xs text-right whitespace-nowrap">
                      {isNonPo && !(isEdit && isLocked) ? (
                        <button
                          type="button"
                          onClick={() => onRemoveItem(it.product_unit_id)}
                          className="px-3 py-2 text-[11px] font-semibold text-white rounded-lg bg-red-600 hover:bg-red-500"
                        >
                          Remove
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-neutral-500">
            * POST akan menambah qty_received di PO items dan update status PO (PARTIAL_RECEIVED / RECEIVED).
          </div>
        </div>
      </div>
    </div>
  )
}
